import {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';

import { useOffers } from './useOffers';
import { useSplitTestBranch } from '../../lendingApi/lendingApiQueries';
import { useContextStateKey } from '../useContextStateKey';
import { useSignupParams } from '../useSignupParams';
import { useSignupRouterDE } from '../useSignupRouter/useSignupRouterDE';

const OffersDEContext = createContext<
  | {
      signupError: ReturnType<ReturnType<typeof useOffers>['getSignupError']>;
      offers: ReturnType<typeof useOffers>['offers'];
      documentRequirements: ReturnType<
        ReturnType<typeof useOffers>['getDocumentRequirements']
      >;
      handleDecision: () => Promise<void>;
      isDeciding: boolean;
      isLoadingOffers: boolean;
      isRefetchingOffers: boolean;
      isLoadingOrRedirecting: boolean;
      isSyncingState: boolean;
      setIsSyncingState: Dispatch<SetStateAction<boolean>>;
    }
  | undefined
>(undefined);

type TProps = {
  children: React.ReactNode;
};

export const OffersDEProvider = ({ children }: TProps) => {
  const [isDeciding, setIsDeciding] = useState(false);
  const [isSyncingState, setIsSyncingState] = useState(false);

  const {
    isLoadingOffers,
    getSignupError,
    offers,
    getDocumentRequirements,
    checkDocumentsRequired,
    refetchOffers,
    isRefetchingOffers,
    offerStatus,
  } = useOffers({ enabled: true });

  const stateKey = useContextStateKey();
  const { navigateSignupDE, navigateToAccountPages, currentStep } =
    useSignupRouterDE();

  const { affiliateTag } = useSignupParams();

  const hasDocumentsRequired = checkDocumentsRequired(
    getDocumentRequirements(offers),
  );

  const isEbayCustomer = affiliateTag === 'ebay';

  const {
    splitTestBranch: eligibilityTestBranch,
    isLoading: eligibilityTestLoading,
  } = useSplitTestBranch('de_eligibility_page_test_2025_q3', {
    enabled: hasDocumentsRequired && !isEbayCustomer,
    cookieOverride: true,
  });

  // Redirection useEffect
  useEffect(() => {
    if (
      currentStep !== 'angebote' ||
      !stateKey ||
      isLoadingOffers ||
      isRefetchingOffers ||
      !offers ||
      isDeciding ||
      eligibilityTestLoading
    ) {
      return;
    }

    const signupError = getSignupError(offers);

    if (signupError && signupError !== 'complete_personal_credit_check') {
      return;
    }

    if (eligibilityTestBranch === 'INCLUDE') {
      void navigateSignupDE('eignungscheck', stateKey);
    } else if (hasDocumentsRequired) {
      void navigateSignupDE('schritt-3', stateKey);
    } else {
      void navigateToAccountPages(stateKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingOffers, offers, stateKey, eligibilityTestLoading]);

  const handleDecision = async () => {
    setIsDeciding(true);

    const refetchedOffers = await refetchOffers();

    const hasRefetchedDocumentsRequired = checkDocumentsRequired(
      getDocumentRequirements(refetchedOffers),
    );

    if (!hasRefetchedDocumentsRequired && stateKey) {
      await navigateToAccountPages(stateKey);
    }

    setIsDeciding(false);
  };

  return (
    <OffersDEContext.Provider
      value={{
        signupError: getSignupError(offers),
        offers,
        documentRequirements: getDocumentRequirements(offers),
        handleDecision,
        isDeciding,
        isLoadingOffers,
        isRefetchingOffers,
        isLoadingOrRedirecting:
          isLoadingOffers ||
          !offers ||
          getSignupError(offers) === 'complete_personal_credit_check' ||
          offerStatus === 'declined' ||
          offerStatus === 'deferred',
        isSyncingState,
        setIsSyncingState,
      }}
    >
      {children}
    </OffersDEContext.Provider>
  );
};

export function useOffersDE() {
  const context = useContext(OffersDEContext);
  if (context === undefined) {
    throw new Error('useOffersDE must be used within the OffersDEProvider');
  }
  return context;
}
